import { RouterProvider } from "@tanstack/react-router";
import "./index.css";
import { router } from "./router";
import { PostHogProvider } from "posthog-js/react";
import { getEnvVar } from "@/common/utils/environment";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./ajax/queryClient";
import { trpcClient, TRPCProvider } from "./trpc";
import { ForceAppUpdateGate } from "@/components/ForceAppUpdateGate";
import { redactInviteTokensFromUrl } from "@/features/provider-invite/utils/invite-token-redaction";
import type { BeforeSendFn, PostHogConfig } from "posthog-js";
import { createSupabaseUrlRedactor } from "@/common/utils/supabase-url-redaction";
import { clearImpersonationOnSignOut } from "@/features/impersonation/impersonation-session";

clearImpersonationOnSignOut();

const redactPostHogEvent: BeforeSendFn = (event) => {
  if (!event) {
    return null;
  }

  for (const key of ["$current_url", "$referrer"]) {
    const value = event.properties?.[key];
    if (typeof value === "string") {
      event.properties![key] = redactInviteTokensFromUrl(value);
    }
  }
  return event;
};

const supabaseUrls = createSupabaseUrlRedactor(
  getEnvVar("VITE_PUBLIC_SUPABASE_URL"),
);

const options: Partial<PostHogConfig> = {
  api_host: getEnvVar("VITE_PUBLIC_POSTHOG_HOST"),
  before_send: redactPostHogEvent,
  // Session recording is enabled per project, not here. If it is on, block the
  // same credential media Sentry blocks and keep signed storage tokens out of
  // the recorded network requests.
  session_recording: {
    blockSelector: "[data-sentry-block]",
    maskCapturedNetworkRequestFn: (request) => ({
      ...request,
      name: supabaseUrls.redactUrl(request.name),
    }),
  },
};

export const Main = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <PostHogProvider
          apiKey={getEnvVar("VITE_PUBLIC_POSTHOG_KEY")}
          options={options}
        >
          <ForceAppUpdateGate>
            <RouterProvider router={router} />
          </ForceAppUpdateGate>
        </PostHogProvider>
      </TRPCProvider>
    </QueryClientProvider>
  );
};
